import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  SPAWN_MARGIN,
  DIFFICULTY,
  COIN,
  ELITE,
  ELITE_SHOOTER,
  ELITE_BOMBER,
  ELITE_GUARDIAN,
  BOSS,
  BOSS_TYPES,
  PLAYER_FX,
  GAME_MODES,
  WEATHER,
  WEATHER_FX,
  BULLET_BUFF,
  type ActiveSkillKey,
  type UltSkillKey,
  type GameModeKey,
  type WeatherKind,
} from '../config/GameConfig';

import Player from '../entities/Player';
import Enemy from '../entities/Enemy';
import type Bullet from '../entities/Bullet';
import type Chest from '../entities/Chest';
import type BuffChest from '../entities/BuffChest';
import type Coin from '../entities/Coin';
import BossProjectile from '../entities/BossProjectile';

import { buildAllTextures } from '../systems/AssetFactory';
import Spawner from '../systems/Spawner';
import Difficulty from '../systems/Difficulty';
import UpgradeManager from '../systems/UpgradeManager';
import SoundManager from '../systems/SoundManager';
import WeatherManager from '../systems/WeatherManager';
import CombatSystem from '../systems/CombatSystem';
import ComboSystem from '../systems/ComboSystem';
import SkillSystem from '../systems/SkillSystem';
import { loadHighScore, saveHighScore, hasSeenHint, markHintSeen, incCampaignClears } from '../systems/highScore';

import HUD from '../ui/HUD';
import Effects from '../ui/Effects';
import PanelManager from '../ui/PanelManager';
import Background from '../ui/Background';

/**
 * MainScene —— 主游戏场景（精简版）
 *
 * 拆分后这里只保留：游戏循环、碰撞、生命周期、状态机（暂停/死亡）。
 * UI 渲染 → HUD；DOM 面板 → PanelManager；特效 → Effects；背景 → Background。
 */
export default class MainScene extends Phaser.Scene {
  // 系统
  private background!: Background;
  private hud!: HUD;
  private effects!: Effects;
  private panels!: PanelManager;
  private sound2!: SoundManager;
  private difficulty!: Difficulty;
  private spawner!: Spawner;
  private upgradeMgr!: UpgradeManager;
  private weather!: WeatherManager;
  private combat!: CombatSystem;
  private combo!: ComboSystem;
  private skills!: SkillSystem;

  // 实体 + 物理组
  private player!: Player;
  private bulletGroup!: Phaser.Physics.Arcade.Group;
  private enemyGroup!: Phaser.Physics.Arcade.Group;
  private chestGroup!: Phaser.Physics.Arcade.Group;
  /** 元素 buff 箱子组（fire/ice/chain）—— 与普通 chestGroup 分开，不计入升级阈值 */
  private bulletBuffChestGroup!: Phaser.Physics.Arcade.Group;
  private coinGroup!: Phaser.Physics.Arcade.Group;
  private obstacleGroup!: Phaser.Physics.Arcade.Group;
  private bossBulletGroup!: Phaser.Physics.Arcade.Group;

  // 状态
  private score = 0;
  private kills = 0;
  private highScore = 0;
  private _isDead = false;
  private _paused = false;
  /** 本局游戏模式（campaign / endless）；通关判定 + 最高分分桶都用它 */
  private _mode: GameModeKey = 'endless';
  /** 关卡模式是否已通关（避免重复触发） */
  private _campaignCleared = false;
  // Combo 状态由 this.combo (ComboSystem) 全权管理 —— 不在这里再存一份

  constructor() {
    super('MainScene');
  }

  preload(): void {
    buildAllTextures(this);
  }

  create(): void {
    this.score = 0;
    this.kills = 0;
    // 模式：MenuScene 写入 registry；没写就回退到 endless（保持老行为）
    this._mode = (this.registry.get('gameMode') as GameModeKey) || 'endless';
    this._campaignCleared = false;
    this.highScore = loadHighScore(this._mode);
    this._isDead = false;
    this._paused = false;
    // combo 实例在下面 system 块里 new 完后再 reset；这里不需要做事

    // 物理组
    this.bulletGroup = this.physics.add.group({ runChildUpdate: false });
    this.enemyGroup = this.physics.add.group({ runChildUpdate: false });
    this.chestGroup = this.physics.add.group({ runChildUpdate: false });
    this.bulletBuffChestGroup = this.physics.add.group({ runChildUpdate: false });
    this.coinGroup = this.physics.add.group({ runChildUpdate: false });
    this.bossBulletGroup = this.physics.add.group({ runChildUpdate: false });
    this.obstacleGroup = this.physics.add.group({ runChildUpdate: false });

    // 玩家 + 难度
    this.player = new Player(this, this.bulletGroup);
    this.difficulty = new Difficulty(this);
    this.difficulty.onTierUp = (tier) => this._onTierUp(tier);

    // 背景 + UI（HUD 需要拿 difficulty）
    this.background = new Background(this, this.difficulty, this.obstacleGroup);
    this.hud = new HUD(this, this.player, this.difficulty, this.highScore);
    this.hud.onPauseClick = () => this._pauseGame();
    // 注：onDashClick / onA2Click / onUltClick 在 SkillSystem 创建后再绑定（见下面）
    // HUD 按钮区注册成 Player 输入死区，避免点按钮时玩家朝按钮跑
    for (const z of this.hud.getInputDeadZones()) {
      this.player.addInputDeadZone(z.x, z.y, z.r);
    }
    this.effects = new Effects(this);
    // 防御：上一局可能因 hit-pause + scene 重启留下错误 timeScale —— 强制还原
    this.physics.world.timeScale = 1;
    this.panels = new PanelManager(this);
    this.sound2 = new SoundManager();

    // 系统
    this.spawner = new Spawner(
      this,
      this.enemyGroup,
      this.chestGroup,
      this.coinGroup,
      this.difficulty,
      this.player, // 传入玩家：怪物出生时根据其位置带水平偏向
      this.bulletBuffChestGroup,
    );
    this.upgradeMgr = new UpgradeManager(this, this.player, {
      sound: this.sound2,
      // 让升级抽卡按当前 tier 提升高稀有度概率（开局基本只出普通，后期才有传说）
      getTier: () => this.difficulty.tier,
      onOpen: () => {
        this.spawner.pause();
        this.difficulty.pause();
      },
      onClose: () => {
        this.spawner.resume();
        this.difficulty.resume();
        this.hud.refreshHearts();
        this._refreshHud();
      },
      onNuke: () => this._doNuke(),
      onHeal: () => {
        // 升级面板还开着时也立刻刷新心条，关闭时再刷一次也没坏处
        this.hud.refreshHearts();
      },
    });

    // 天气系统：每次升档由 _onTierUp 调 rollNext；陨石回调由 weather 触发
    this.weather = new WeatherManager(this);
    this.weather.onChange = (kind) => this._onWeatherChange(kind);
    this.weather.onMeteor = (x, y) => this._spawnMeteor(x, y);

    // Combo 系统：连杀计数 + 奖励 + HUD 联动
    this.combo = new ComboSystem({
      onShow: (n) => this.hud.showCombo(n),
      onHide: () => this.hud.hideCombo(),
      onCoinReward: () => { this.player.addCoins(1); this._refreshHud(); },
      onBreakBonus: (bonus) => {
        this.score += bonus;
        this.hud.setScore(this.score);
        this.hud.setHighScoreLive(this.score, this.highScore);
      },
    });

    // 战斗系统：玩家弹药命中处理（暴击 / 元素 / 反弹 / 吸血）
    this.combat = new CombatSystem(this, this.player, this.effects, this.enemyGroup, {
      onEnemyKilled: (enemy) => this._onEnemyKilled(enemy),
      isPaused: () => this._isDead || this._paused,
      comboDamageMul: () => this.combo.damageMul(),
    });

    // 技能系统：从 registry 读 loadout（MenuScene 写入），缺省 dash + pulse + nuke
    const a1Key = (this.registry.get('skillA1') as ActiveSkillKey) || 'dash';
    const a2Key = (this.registry.get('skillA2') as ActiveSkillKey) || 'pulse';
    const ultKey = (this.registry.get('skillUlt') as UltSkillKey) || 'nuke';
    this.skills = new SkillSystem(
      this,
      this.player,
      this.effects,
      {
        getEnemyGroup: () => this.enemyGroup,
        getBossBulletGroup: () => this.bossBulletGroup,
        onEnemyKilled: (enemy) => this._onEnemyKilled(enemy),
        isLocked: () => this._isDead || this._paused || this.upgradeMgr.isOpen(),
      },
      a1Key, a2Key, ultKey,
      this.sound2,
    );
    // HUD 点击 → SkillSystem 触发
    this.hud.onDashClick = () => this.skills.fireA1();
    this.hud.onA2Click   = () => this.skills.fireA2();
    this.hud.onUltClick  = () => this.skills.fireUlt();

    // 碰撞
    this.physics.add.overlap(this.player, this.obstacleGroup, this._onPlayerHitObstacle, undefined, this);
    this.physics.add.collider(this.enemyGroup, this.obstacleGroup);
    this.physics.add.collider(this.bulletGroup, this.obstacleGroup, this._onBulletHitObstacle, undefined, this);
    this.physics.add.collider(this.bossBulletGroup, this.obstacleGroup, this._onBossBulletHitObstacle, undefined, this);
    this.physics.add.overlap(this.bulletGroup, this.enemyGroup, this._onBulletHitEnemy, undefined, this);
    this.physics.add.overlap(this.player, this.enemyGroup, this._onPlayerHitEnemy, undefined, this);
    this.physics.add.overlap(this.player, this.chestGroup, this._onPlayerHitChest, undefined, this);
    this.physics.add.overlap(this.player, this.bulletBuffChestGroup, this._onPlayerHitBuffChest, undefined, this);
    this.physics.add.overlap(this.player, this.coinGroup, this._onPlayerHitCoin, undefined, this);
    this.physics.add.overlap(this.player, this.bossBulletGroup, this._onPlayerHitBossBullet, undefined, this);

    this.hud.refreshHearts();
    this._refreshHud();

    // 首次引导：渐进 toast（每条只触发一次，存 localStorage）
    // - move-rule:    开局 1.0s 后："拖动屏幕移动"
    // - chest-rule:   开局 4.0s 后："撞 3 个宝箱解锁升级"（让玩家先打几下再弹）
    // - dash-rule:    开局 9.0s 后："右下 ⚡ 闪现 + 0.5s 无敌"
    // - 其余（精英 / Boss / 元素弹）按事件触发，分别在对应 hook 里弹
    this._scheduleOnboardToast('move-split', 1000, '👆 拖动移动 · 右下角竖列三按钮 = 大招/技能 2/技能 1');
    this._scheduleOnboardToast('chest-rule', 4000, `📦 收集 ${this.upgradeMgr.threshold} 个金色宝箱解锁升级`);
    this._scheduleOnboardToast('dash-rule',  9000, '⚡ 点击右下角按钮释放技能');

    // 场景切换时清理 DOM 面板
    this.events.once('shutdown', () => this._cleanupDom());
    this.events.once('destroy', () => this._cleanupDom());

    // 键盘快捷键：A1=SPACE/Z，A2=X，ULT=C（PC 端方便，手机直接点 HUD 按钮）
    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-SPACE', () => this.skills.fireA1());
      kb.on('keydown-Z', () => this.skills.fireA1());
      kb.on('keydown-X', () => this.skills.fireA2());
      kb.on('keydown-C', () => this.skills.fireUlt());
    }
  }

  // 旧 _tryDash 已废弃 —— 闪现现在走 SkillSystem.fireA1() 路径


  private _cleanupDom(): void {
    if (this.upgradeMgr.isOpen()) this.upgradeMgr.close();
    this.panels.closeAll();
  }

  private _goMenu(): void {
    this.physics.world.timeScale = 1;
    if (this.physics.world.isPaused) this.physics.resume();
    this.spawner?.resume();
    this.difficulty?.resume();
    this.panels?.closeAll();
    this.highScore = saveHighScore(this.score, this._mode);
    this.scene.stop('MainScene');
    this.scene.start('MenuScene');
  }

  /** 同时刷新 stats 和宝箱进度行，外部所有刷新都走这里 */
  private _refreshHud(): void {
    this.hud.refreshStats(this.kills, {
      collected: this.upgradeMgr.chestCollected,
      threshold: this.upgradeMgr.threshold,
    });
  }

  /**
   * 引导 toast：每个 id 只触发一次（存 localStorage seenHints）；延迟 ms 后弹出。
   * 暂停 / 死亡时跳过本次（下次进游戏会重新计时）。
   */
  private _scheduleOnboardToast(id: string, delayMs: number, text: string): void {
    if (hasSeenHint(id)) return;
    this.time.delayedCall(delayMs, () => {
      if (this._isDead || this._paused) return;
      this.hud.showHintToast(text, 4500);
      markHintSeen(id);
    });
  }

  /** 事件型引导：第一次遇到精英 / Boss / 元素弹时弹一次性提示 */
  private _maybeOnboardToast(id: string, text: string): void {
    if (hasSeenHint(id)) return;
    if (this._isDead || this._paused) return;
    this.hud.showHintToast(text, 4500);
    markHintSeen(id);
  }

  // ─── 主循环 ───────────────────────────────────────────────────────

  override update(time: number, delta: number): void {
    if (this._isDead || this._paused) return;

    this.background.update(delta);
    this.player.stepMove(delta);
    this.player.syncShieldRing();
    this._updateCoinMagnet(delta);
    // 每帧刷新闪现按钮冷却（即使暂停时也刷一次，让玩家看到 CD 在转）
    // 每帧刷新技能槽 HUD（即使暂停时也刷一次，让玩家看到 CD 在转）
    // 同时让 SkillSystem 自己 tick（timewarp / frenzy 到期还原）
    this.skills.update(delta);
    this.hud.refreshSkillSlots(this.skills.snapshot());

    if (!this.upgradeMgr.isOpen()) {
      if (this.player.tryShoot(time, this.weather.fireRateMul())) this.sound2.shoot();
      // Boss 在场时暂停波数推进：Boss 战是"事件"，不该一边打 Boss 一边推下一波。
      // Spawner.update 自己也会 hasActiveBoss() 时停刷常规怪 —— 这里同步停 Difficulty 计时
      // 即可保证 _onTierUp / Boss 编排不会撞车。
      if (!this.spawner.hasActiveBoss()) {
        this.difficulty.update(time, delta);
      }
      this.spawner.update(time, delta);
      this.weather.update(delta);
      this._applyWindForces(delta);
      this._tickCombo();

      // 普通怪追击玩家 + Boss 攻击 tick：合并到一次 forEach 里少扫一遍数组
      // 同时记录"主 Boss"（非 mini）用来刷新顶部全局血条
      let primaryBoss: Enemy | null = null;
      this.enemyGroup.getChildren().forEach((e) => {
        const enemy = e as Enemy;
        if (enemy.isBoss) {
          enemy.bossUpdate(delta, (boss) => this._onBossAttack(boss));
          if (!enemy.isMiniBoss && !primaryBoss) primaryBoss = enemy;
        } else {
          enemy.chaseUpdate(delta, this.player);
          if (enemy.isShooter) {
            enemy.shooterUpdate(delta, (s) => this._onShooterAttack(s));
            this._maybeOnboardToast('mob-shooter', '🟠 橙色射手会朝你打子弹，先解决它');
          }
          if (enemy.isBomber) {
            enemy.bomberUpdate(delta, this.player, (b) => this._onBomberExplode(b));
            this._maybeOnboardToast('mob-bomber', '🔴 红色自爆兵会贴脸引爆，抢先打死它');
          }
          if (enemy.isGuardian) {
            this._maybeOnboardToast('mob-guardian', '🛡 青色护盾兵正面减伤，需要穿透弹');
          } else if (enemy.isElite) {
            this._maybeOnboardToast('mob-elite', '🟣 紫色精英 HP 厚分多，掉金币也多');
          }
        }
      });
      // 顶部全屏 Boss 血条同步：仅追主 Boss（mini 不显示，单独头顶小血条已够用）
      if (primaryBoss) {
        const b: Enemy = primaryBoss;
        this.hud.refreshBossBar(b.hp, b.maxHp, b.phase);
      } else {
        this.hud.hideBossBar();
      }

      // 跟踪弹（玩家子弹）
      if (this.player.bulletHoming) {
        this.bulletGroup.getChildren().forEach((b) => {
          (b as Bullet).homingUpdate(delta, this.enemyGroup);
        });
      }

      // Boss 子弹追击玩家
      this.bossBulletGroup.getChildren().forEach((p) => {
        (p as BossProjectile).homingUpdate(delta, this.player);
      });
    }

    this._cullOffscreen();
  }

  /**
   * 玩家 magnetRadius 内的金币朝玩家加速并 cap 到 magnetMaxSpeed。
   * 距离越近力越大（线性插值），让金币越靠近越"贴脸飞"。
   */
  private _updateCoinMagnet(delta: number): void {
    if (!this.weather.coinMagnetEnabled()) return;
    const dt = delta / 1000;
    const px = this.player.x;
    const py = this.player.y;
    const r = COIN.magnetRadius;
    const r2 = r * r;

    const coins = this.coinGroup.getChildren();
    for (let i = 0; i < coins.length; i++) {
      const c = coins[i] as Coin;
      if (!c.body) continue;
      const dx = px - c.x;
      const dy = py - c.y;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2) continue;
      const d = Math.sqrt(d2) || 1;
      const k = 1 + (1 - d / r);
      const ax = (dx / d) * COIN.magnetAccel * k;
      const ay = (dy / d) * COIN.magnetAccel * k;
      c.body.velocity.x += ax * dt;
      c.body.velocity.y += ay * dt;
      const sp = Math.hypot(c.body.velocity.x, c.body.velocity.y);
      if (sp > COIN.magnetMaxSpeed) {
        c.body.velocity.x = (c.body.velocity.x / sp) * COIN.magnetMaxSpeed;
        c.body.velocity.y = (c.body.velocity.y / sp) * COIN.magnetMaxSpeed;
      }
    }
  }

  private _cullOffscreen(): void {
    /**
     * 出屏剔除 —— 横竖都要查。
     *
     * 早期版本只看 y，狂风天气下金币 / 普通怪可能被横向吹出屏外却继续飘着，
     * 既浪费物理 tick 又会被磁吸忽悠拉回（视觉上像"返场"）。这里多给 50px 余量做边界，
     * 避免刚出生贴边时被自己 cull 掉。
     *
     * Boss 不在剔除集合里（他们 anchor 钉在屏幕中央，不可能出 x 边界）。
     */
    const LEFT = -50;
    const RIGHT = GAME_WIDTH + 50;
    const cull = (group: Phaser.Physics.Arcade.Group, top: number, bottom: number): void => {
      const arr = group.getChildren();
      for (let i = arr.length - 1; i >= 0; i--) {
        const o = arr[i] as Phaser.GameObjects.GameObject & { x: number; y: number; destroy: () => void };
        if (o.y < top || o.y > bottom || o.x < LEFT || o.x > RIGHT) o.destroy();
      }
    };
    cull(this.bulletGroup, -40, GAME_HEIGHT + 40);
    cull(this.enemyGroup, -200, GAME_HEIGHT + 80);
    cull(this.chestGroup, -200, GAME_HEIGHT + 60);
    cull(this.bulletBuffChestGroup, -200, GAME_HEIGHT + 60);
    cull(this.coinGroup, -200, GAME_HEIGHT + 60);
    cull(this.bossBulletGroup, -40, GAME_HEIGHT + 60);
  }

  // ─── 难度升档 ──────────────────────────────────────────────────────

  private _onTierUp(tier: number): void {
    this._refreshHud();
    this.hud.showWaveBanner(tier);

    // 战场天气：每波重新滚一次（tier 0 开局静默）
    this.weather.rollNext(tier);

    // 升档奖励：错开下落 2 个宝箱
    for (let i = 0; i < DIFFICULTY.giftChestsPerTier; i++) {
      this.time.delayedCall(i * DIFFICULTY.giftChestStagger, () => {
        if (this._isDead) return;
        // 全屏自由战场：礼物宝箱在屏幕左/右两个固定位置错开下落
        const segments = DIFFICULTY.giftChestsPerTier;
        const usable = GAME_WIDTH - SPAWN_MARGIN * 2;
        const x = SPAWN_MARGIN + (usable * (i + 0.5)) / segments;
        this.spawner.dropChest({ x });
      });
    }

    // 关卡模式：第 5 / 10 / 15 波是预先编排的 Boss 战
    if (this._mode === 'campaign') {
      const wave = tier + 1;
      if (wave === 5)  { this._scheduleCampaignBoss('wave5');  return; }
      if (wave === 10) { this._scheduleCampaignBoss('wave10'); return; }
      if (wave === 15) { this._scheduleCampaignBoss('wave15'); return; }
      // 关卡模式非 Boss 波直接结束（不走无尽的"每 5 波一只"）
      return;
    }

    // 无尽模式：每 N 档刷一只 Boss
    if (DIFFICULTY.bossEveryNTiers > 0 && tier % DIFFICULTY.bossEveryNTiers === 0) {
      this.hud.showBossBanner();
      this.time.delayedCall(800, () => {
        if (this._isDead) return;
        const boss = this.spawner.spawnBoss();
        boss.onPhaseChange = (b, phase) => {
          if (phase === 2) this._onBossEnrage(b);
        };
        this._showBossBarFor(boss);
        this.sound2.bossArrive();
      });
    }
  }

  /** Boss 出场后挂顶部全屏血条 —— 用 Boss 类型的中文名 */
  private _showBossBarFor(boss: Enemy): void {
    if (boss.isMiniBoss) return; // 分裂出来的小 boss 不占血条
    const cfg = BOSS_TYPES[boss.bossType] ?? BOSS_TYPES.standard;
    this.hud.showBossBar(cfg.label);
    this._maybeOnboardToast('boss-rule', '★ Boss HP 50% 时进入狂暴，弹更密更猛');
  }

  /**
   * 关卡模式 Boss 编排（每只都接 onPhaseChange / 关卡通关由 _onEnemyKilled 触发）
   *
   *   wave5  : 标准 Boss（教程级，体验 Boss 节奏）
   *   wave10 : 双 Boss（standard + zigzag 同屏，HP 各 ×0.7 互相牵制）
   *   wave15 : Final Boss = splitter，HP × finalBossHpMul，分裂出 4 只更猛的 mini
   */
  private _scheduleCampaignBoss(stage: 'wave5' | 'wave10' | 'wave15'): void {
    this.hud.showBossBanner();
    this.time.delayedCall(800, () => {
      if (this._isDead) return;
      this.sound2.bossArrive();

      const hookEnrage = (b: Enemy) => {
        b.onPhaseChange = (eb, phase) => { if (phase === 2) this._onBossEnrage(eb); };
      };

      if (stage === 'wave5') {
        const b = this.spawner.spawnBoss('standard');
        hookEnrage(b);
        this._showBossBarFor(b);
        return;
      }
      if (stage === 'wave10') {
        // 双 Boss：standard 和 zigzag 错开 x 出场，HP 各打 0.7（合 1.4 仍比单 Boss 厚 40%）
        const b1 = this.spawner.spawnBoss('standard');
        b1.maxHp = Math.round(b1.maxHp * 0.7);
        b1.hp = b1.maxHp;
        b1.x = GAME_WIDTH * 0.3;
        hookEnrage(b1);
        // 双 Boss 暂用先出场的那只挂血条（合并显示意义不大，先这样）
        this._showBossBarFor(b1);

        this.time.delayedCall(400, () => {
          if (this._isDead) return;
          const b2 = this.spawner.spawnBoss('zigzag');
          b2.maxHp = Math.round(b2.maxHp * 0.7);
          b2.hp = b2.maxHp;
          b2.x = GAME_WIDTH * 0.7;
          hookEnrage(b2);
        });
        return;
      }
      // wave15 Final：splitter + HP×1.8 + 死时分裂数升到 6（在 _onEnemyKilled 已有 splitter mini 触发）
      const b = this.spawner.spawnBoss('splitter');
      const mul = GAME_MODES.campaign.finalBossHpMul;
      if (mul > 1) {
        b.maxHp = Math.round(b.maxHp * mul);
        b.hp = b.maxHp;
      }
      hookEnrage(b);
      this._showBossBarFor(b);
    });
  }

  /** Boss 进入二阶段时的全场反馈 */
  private _onBossEnrage(boss: Enemy): void {
    if (!boss.active || boss._dead) return;
    this.hud.showEnrageBanner();
    this.cameras.main.shake(280, 0.014);
    this.sound2.bossArrive();
    // boss 周围一圈红色冲击波，提示"狂暴了别贴脸"
    this.effects.explosion(boss.x, boss.y, { big: true, color: 0xff5566, count: 24 });
  }

  private _onBossAttack(boss: Enemy): void {
    if (!boss.active || boss._dead) return;
    const x = boss.x;
    const y = boss.y + boss.body.halfHeight;
    // 二阶段：扇形多 phase2SalvoBonus 颗、跨度 +phase2SpreadAdd
    const isPhase2 = boss.phase === 2 && !boss.isMiniBoss;
    const n = Math.max(1, BOSS.salvoCount + (isPhase2 ? BOSS.phase2SalvoBonus : 0));
    const spread = BOSS.salvoSpread + (isPhase2 ? BOSS.phase2SpreadAdd : 0);
    // 二阶段弹速反而下调（弹更密 + 更散，再加速就没法躲了）
    const speed = BOSS.projectileSpeed * (isPhase2 ? BOSS.phase2ProjectileSpeedMul : 1);
    // 扇形：n 颗子弹在 [-spread/2, +spread/2] 内等距分布，正下方为 0
    for (let i = 0; i < n; i++) {
      const angle = n === 1 ? 0 : -spread / 2 + (spread * i) / (n - 1);
      const proj = new BossProjectile(this, x, y);
      this.bossBulletGroup.add(proj);
      // 防御：加入 group 时 Phaser 可能重置 body，velocity 在这里再打一次（与 Bullet 一致）
      proj.body.setVelocity(Math.sin(angle) * speed, Math.cos(angle) * speed);
      proj.body.setAllowGravity(false);
    }
  }

  /**
   * 射手精英开火：朝玩家方向打一发直线弹
   * 用 BossProjectile + homing=false 复用渲染/物理，但着色 + 伤害走 ELITE_SHOOTER 配置
   */
  private _onShooterAttack(shooter: Enemy): void {
    if (!shooter.active || shooter._dead) return;
    const sx = shooter.x;
    const sy = shooter.y + shooter.body.halfHeight;
    const dx = this.player.x - sx;
    const dy = this.player.y - sy;
    const dist = Math.hypot(dx, dy) || 1;
    const speed = ELITE_SHOOTER.projectileSpeed;
    const proj = new BossProjectile(this, sx, sy, {
      speed,
      damage: ELITE_SHOOTER.projectileDamage,
      tint: ELITE_SHOOTER.projectileColor,
      homing: false,
      scale: 0.75,
    });
    this.bossBulletGroup.add(proj);
    proj.body.setVelocity((dx / dist) * speed, (dy / dist) * speed);
    proj.body.setAllowGravity(false);
  }

  /**
   * 自爆精英引爆：以怪物中心为圆心，explodeRadius 圆形 AOE
   *   - 玩家在范围内 → 标准 takeDamage 走护盾/无敌窗口；命中后播 playerHurt 提示
   *   - 视觉：白色冲击波环 + 大爆炸 + 屏幕震动
   *   - 怪物自身按"被打死"处理（走 _onEnemyKilled 让分数/金币正常发生）
   */
  private _onBomberExplode(bomber: Enemy): void {
    if (!bomber.active || bomber._dead) return;
    const cx = bomber.x;
    const cy = bomber.y;
    const r = ELITE_BOMBER.explodeRadius;

    // 视觉：先大爆炸 + 白色扩张冲击环，再让怪物消失
    this.effects.explosion(cx, cy, { big: true, color: 0xff5566, count: 22 });
    const ring = this.add
      .circle(cx, cy, 20, 0xff5566, 0)
      .setStrokeStyle(4, 0xff8888, 1)
      .setDepth(160);
    this.tweens.add({
      targets: { rad: 20 },
      rad: r,
      duration: 280,
      ease: 'Cubic.easeOut',
      onUpdate: (tw, t: { rad: number }) => {
        ring.setRadius(t.rad);
        ring.setStrokeStyle(4, 0xff8888, 1 - tw.progress);
      },
      onComplete: () => ring.destroy(),
    });

    // AOE 命中玩家
    const dx = this.player.x - cx;
    const dy = this.player.y - cy;
    if (dx * dx + dy * dy <= r * r) {
      const result = this.player.takeDamage(ELITE_BOMBER.damage);
      if (result.blocked) {
        if (result.byShield) { this.sound2.shieldBreak(); this._onShieldBroken(); }
      } else {
        this.sound2.hurt();
        this.effects.playerHurt(this.player.x, this.player.y, ELITE_BOMBER.damage);
        this.skills?.onHurt();
        this.hud.refreshHearts();
        this._breakCombo();
        if (result.died) this._gameOver();
      }
    }

    // 怪物自身走标准死亡：分数 / 金币 / 爆炸都正常发生
    bomber._dead = true;
    this._onEnemyKilled(bomber);
  }

  // ─── 碰撞 ─────────────────────────────────────────────────────────

  private _onBulletHitEnemy = (bulletObj: unknown, enemyObj: unknown): void => {
    this.combat.handleBulletHitEnemy(bulletObj as Bullet, enemyObj as Enemy);
    this.sound2.hit();
    // CombatSystem 命中后可能改了 player.hp（吸血），统一在这里刷一次心条
    this.hud.refreshHearts();
  };

  private _onPlayerHitObstacle = (_playerObj: unknown, obstacleObj: unknown): void => {
    if (this._isDead) return;
    const obstacle = obstacleObj as Phaser.Physics.Arcade.Image;
    const result = this.player.takeDamage(1);

    if (result.blocked) {
      if (result.byShield) { this.sound2.shieldBreak(); this._onShieldBroken(); }
      this.effects.explosion(obstacle.x, obstacle.y, { color: 0xffd166, count: 8 });
      this._refreshHud();
      return;
    }

    this.sound2.hurt();
    this.effects.playerHurt(this.player.x, this.player.y, 1);
    this.skills?.onHurt();
    this.hud.refreshHearts();
    this._breakCombo();
    this._maybeOnboardToast('obstacle-crush', '⚠ 路障会卡住坦克，及时绕开');
    if (result.died) this._gameOver();
  };

  private _onBulletHitObstacle = (bulletObj: unknown): void => {
    const bullet = bulletObj as Bullet;
    if (!bullet.active) return;
    this.effects.explosion(bullet.x, bullet.y, { color: 0xffd166, count: 4 });
    bullet.destroy();
  };

  private _onBossBulletHitObstacle = (projObj: unknown): void => {
    const proj = projObj as BossProjectile;
    if (!proj.active) return;
    this.effects.explosion(proj.x, proj.y, { color: 0xff8a3c, count: 6 });
    proj.destroy();
  };


  private _onEnemyKilled(enemy: Enemy): void {
    this.kills += 1;
    // ULT 蓄能：每次击杀都给（具体加几看 UltConfig，目前所有 ULT 都是 +1）
    this.skills?.onKill();
    this.score += enemy.scoreReward || 10;
    this.hud.setScore(this.score);
    this.hud.setHighScoreLive(this.score, this.highScore);
    this._bumpCombo();

    this.effects.explosion(enemy.x, enemy.y, {
      big: enemy.isBoss,
      color: enemy.isBoss ? 0xff5566 : 0xffaa55,
    });

    if (enemy.isBoss) {
      // Boss 在场判定由 Spawner.hasActiveBoss() 扫 enemyGroup 得出，
      // 这里 enemy.destroy() 之后它会自动从 group 移除，不用手动同步计数
      // （顶部全屏血条由 update 循环每帧根据是否有主 Boss 自动 show/hide，无需在这手动隐藏）
      // 金币散射（mini 少一点）
      const coinN = enemy.isMiniBoss
        ? Math.ceil(COIN.bossDropAmount / 3)
        : COIN.bossDropAmount;
      for (let i = 0; i < coinN; i++) {
        const ang = (Math.PI * 2 * i) / coinN;
        const sx = Math.cos(ang) * 80;
        const sy = Math.sin(ang) * 80 - 40;
        this.spawner.dropCoin(enemy.x, enemy.y, { vx: sx, vy: sy });
      }
      this.sound2.bossKill();

      // splitter 父体死亡时分裂
      const isSplitterParent = enemy.bossType === 'splitter' && !enemy.isMiniBoss;
      if (isSplitterParent) {
        // 关卡模式 Final（wave15）多分裂 2 只，让"满屏 mini"成为通关瞬间的高潮
        const isFinalSplitter = this._mode === 'campaign'
          && this.difficulty.tier + 1 >= GAME_MODES.campaign.totalWaves;
        const n = (BOSS_TYPES.splitter.splitOnDeath ?? 4) + (isFinalSplitter ? 2 : 0);
        for (let i = 0; i < n; i++) {
          const offX = (i - (n - 1) / 2) * 80;
          const mini = this.spawner.spawnMiniBoss(enemy.x + offX, enemy.y, enemy.maxHp);
          mini.body.velocity.x = (i === 0 ? -1 : 1) * 60;
        }
      }

      // 只有非 mini Boss 才掉宝箱
      if (!enemy.isMiniBoss) {
        for (let i = 0; i < DIFFICULTY.bossDropChests; i++) {
          this.time.delayedCall(i * 200, () => {
            if (this._isDead) return;
            this.spawner.dropChest();
          });
        }
      }
    } else {
      // 精英系（普通精英 / 射手 / 自爆 / 护盾）：固定散射金币
      const coinN = enemy.isElite ? ELITE.coinDrop
        : enemy.isShooter ? ELITE_SHOOTER.coinDrop
        : enemy.isBomber ? ELITE_BOMBER.coinDrop
        : enemy.isGuardian ? ELITE_GUARDIAN.coinDrop
        : 0;
      if (coinN > 0) {
        for (let i = 0; i < coinN; i++) {
          const ang = (Math.PI * 2 * i) / coinN + Math.random() * 0.4;
          this.spawner.dropCoin(enemy.x, enemy.y, {
            vx: Math.cos(ang) * 90,
            vy: Math.sin(ang) * 90 - 30,
          });
        }
      } else if (Math.random() < COIN.dropRate) {
        this.spawner.dropCoin(enemy.x, enemy.y, {
          vx: Phaser.Math.Between(-40, 40),
          vy: -50,
        });
      }
      this.sound2.kill();
    }

    const wasFinalBoss = enemy.isBoss && !enemy.isMiniBoss
      && this._mode === 'campaign'
      && !this._campaignCleared
      && this.difficulty.tier + 1 >= GAME_MODES.campaign.totalWaves;

    enemy.destroy();
    this._refreshHud();

    if (wasFinalBoss) {
      this._campaignCleared = true;
      // 延后 1.2s 让爆炸 / 分裂动画播完再弹胜利面板，体验更有节奏
      this.time.delayedCall(1200, () => this._onCampaignClear());
    }
  }

  /**
   * 关卡模式通关：暂停 spawner / difficulty，弹胜利面板，写最高分 + 累计通关次数。
   * 玩家可以选择"继续"切到无尽延伸（保留 buff），或重开 / 回菜单。
   */
  private _onCampaignClear(): void {
    this.spawner.pause();
    this.difficulty.pause();
    // 不 setIsDead，避免 update 早退；但禁用碰撞防止穿越剩余怪
    this.physics.pause();
    this.highScore = saveHighScore(this.score, this._mode);
    const totalClears = incCampaignClears();

    // 满屏金色喷泉效果
    for (let i = 0; i < 30; i++) {
      const ang = (Math.PI * 2 * i) / 30 + Math.random() * 0.3;
      this.time.delayedCall(i * 30, () => {
        this.spawner.dropCoin(GAME_WIDTH / 2, GAME_HEIGHT / 2, {
          vx: Math.cos(ang) * 220,
          vy: Math.sin(ang) * 220 - 60,
        });
      });
    }
    this.cameras.main.flash(500, 255, 224, 102);
    this.sound2.bossKill();

    this.panels.showVictory(
      {
        score: this.score,
        highScore: this.highScore,
        kills: this.kills,
        wave: this.difficulty.tier + 1,
        coins: this.player.coins,
        totalClears,
      },
      {
        onContinue: () => {
          // 切换为无尽延伸：mode 改成 endless，恢复运行
          this._mode = 'endless';
          this.physics.resume();
          this.spawner.resume();
          this.difficulty.resume();
        },
        onRestart: () => this.scene.restart(),
        onMenu: () => this._goMenu(),
      },
    );
  }

  /**
   * 护盾被打碎时的统一回调（3 处 byShield 命中点共用）
   * - 标准音效已在调用方播放
   * - 若玩家拿了"盾爆"传说卡：在玩家位置炸 120 半径，AOE 伤害 = bulletDamage × 5
   *   （只对屏内非 Boss 怪生效；Boss 也吃伤但不会被一击秒）
   */
  private _onShieldBroken(): void {
    if (!this.player.shieldExplode) return;
    const x = this.player.x;
    const y = this.player.y;
    const r = 120;
    const r2 = r * r;
    const dmg = this.player.bulletDamage * 5;
    // 视觉：白色冲击波 + 屏抖
    this.effects.explosion(x, y, { big: true, color: 0x4cd2ff, count: 24 });
    this.cameras.main.shake(180, 0.012);
    // 实际 AOE
    this.enemyGroup.getChildren().forEach((e) => {
      const en = e as Enemy;
      if (!en.active || en._dead) return;
      const dx = en.x - x;
      const dy = en.y - y;
      if (dx * dx + dy * dy > r2) return;
      const dead = en.takeDamage(dmg);
      if (dead) {
        en._dead = true;
        this._onEnemyKilled(en);
      }
    });
  }

  private _onPlayerHitEnemy = (_playerObj: unknown, enemyObj: unknown): void => {
    const enemy = enemyObj as Enemy;
    if (!enemy.active || enemy._dead) return;
    const result = this.player.takeDamage(1);

    if (result.blocked) {
      if (result.byShield) { this.sound2.shieldBreak(); this._onShieldBroken(); }
      enemy._dead = true;
      this.effects.explosion(enemy.x, enemy.y, { color: 0x4cd2ff });
      enemy.destroy();
      this._refreshHud();
      return;
    }
    enemy._dead = true;
    this.effects.explosion(enemy.x, enemy.y, { big: enemy.isBoss, color: 0xff5566 });
    enemy.destroy();
    this.sound2.hurt();
    this.effects.playerHurt(this.player.x, this.player.y, 1);
    this.skills?.onHurt();
    this.hud.refreshHearts();
    this._breakCombo();
    if (result.died) this._gameOver();
  };

  private _onPlayerHitChest = (_playerObj: unknown, chestObj: unknown): void => {
    const chest = chestObj as Chest;
    if (!chest.active) return;
    const cx = chest.x;
    const cy = chest.y;
    this.effects.explosion(cx, cy, { color: 0xffd166, count: 8 });
    chest.destroy();
    this.sound2.coin();

    this.upgradeMgr.onChestCollected();

    // 拾取飘字 + HUD 进度刷新；如果刚刚开了升级面板就显示"已凑齐"风格
    const collected = this.upgradeMgr.chestCollected;
    const threshold = this.upgradeMgr.threshold;
    // chestCollected 在凑齐时被重置为 0，那一帧用 threshold/threshold 显示更直观
    const display = collected === 0 ? threshold : collected;
    this.hud.showChestPickupText(cx, cy, display, threshold);
    this.hud.refreshStats(this.kills, { collected, threshold });
  };

  /**
   * 撞到元素 buff 箱（fire/ice/chain 独立箱）—— 立即获得 8s 元素弹药效果。
   * 不计入升级阈值，不开升级面板。
   */
  private _onPlayerHitBuffChest = (_playerObj: unknown, chestObj: unknown): void => {
    const chest = chestObj as BuffChest;
    if (!chest.active) return;
    const kind = chest.buffKind;
    const cfg = BULLET_BUFF[kind];
    this.effects.explosion(chest.x, chest.y, { color: cfg.tint, count: 12 });
    chest.destroy();
    this.sound2.upgrade();
    this.player.applyBulletBuff(kind, cfg.durationMs);
    this.hud.showBulletBuffBanner(cfg.label, cfg.desc, cfg.tint);
    this._maybeOnboardToast('elem-bullet', '★ 彩色箱子 = 元素弹（火/冰/电），8 秒限时');
  };

  private _onPlayerHitCoin = (_playerObj: unknown, coinObj: unknown): void => {
    const coin = coinObj as Coin;
    if (!coin.active) return;
    // goldRush 天气：金币 ×2
    this.player.addCoins(COIN.dropAmount * this.weather.coinMul());
    coin.destroy();
    this.sound2.coin();
    this._refreshHud();
  };

  private _onPlayerHitBossBullet = (_playerObj: unknown, projObj: unknown): void => {
    const proj = projObj as BossProjectile;
    if (!proj.active) return;
    const dmg = proj.damage || 1;
    proj.destroy();
    const result = this.player.takeDamage(dmg);
    if (result.blocked) {
      if (result.byShield) { this.sound2.shieldBreak(); this._onShieldBroken(); }
      this._refreshHud();
      return;
    }
    this.sound2.hurt();
    this.effects.playerHurt(this.player.x, this.player.y, dmg);
    this.skills?.onHurt();
    this.hud.refreshHearts();
    this._breakCombo();
    if (result.died) this._gameOver();
  };

  // ─── 天气 / 元素弹 / Combo / 陨石 ───────────────────────────────

  private _onWeatherChange(kind: WeatherKind): void {
    // 把天气对刷怪节奏的影响同步给 Spawner（goldRush 加密）
    this.spawner.rateMul = this.weather.spawnIntervalMul();
    // tier 0（开局）和 'none' 不弹横幅，避免炸场
    if (kind === 'none') return;
    const cfg = WEATHER[kind];
    this.hud.showWeatherBanner(cfg.label, cfg.desc, cfg.tint);
  }

  /**
   * 给"非玩家弹药"统一施加狂风：
   *   - bossBulletGroup（Boss + 射手 + 陨石都在这）
   *   - coinGroup（金币更不需要风，但飘起来更有趣）
   *   - bulletGroup（玩家自己的子弹也要受影响，否则太占便宜）
   * vy 不动，仅修 vx 加偏移；为了不把追踪 Boss 弹的方向算乱，每帧叠风但不直接覆盖。
   */
  private _applyWindForces(delta: number): void {
    const wx = this.weather.windX();
    if (wx === 0) return;
    const dt = delta / 1000;
    const dvx = wx * dt;
    const apply = (group: Phaser.Physics.Arcade.Group) => {
      const arr = group.getChildren();
      for (let i = 0; i < arr.length; i++) {
        const o = arr[i] as Phaser.GameObjects.GameObject & { body: Phaser.Physics.Arcade.Body };
        if (o.body) o.body.velocity.x += dvx;
      }
    };
    apply(this.bossBulletGroup);
    apply(this.bulletGroup);
    apply(this.coinGroup);
  }

  /**
   * Combo wrappers —— 转发到 ComboSystem。
   * 保留方法名是因为 _onEnemyKilled / 玩家受击 等多处仍按动作语义在调用。
   */
  private _tickCombo(): void { this.combo.tick(); }
  private _bumpCombo(): void { this.combo.bump(); }
  private _breakCombo(): void { this.combo.forceBreak(); }

  /**
   * 陨石：屏侧顶部出生，朝玩家所在 x 偏向斜射，复用 BossProjectile（紫红着色 + 略大）
   * 不做追踪（homing=false），因为天气陨石定位是"环境干扰"而非"杀招"
   */
  private _spawnMeteor(x: number, y: number): void {
    const speed = WEATHER_FX.meteor.fallSpeed;
    // 简单瞄准玩家：从屏侧射向玩家 x 略下
    const dx = this.player.x - x;
    const dy = (GAME_HEIGHT - 80) - y;
    const d = Math.hypot(dx, dy) || 1;
    const proj = new BossProjectile(this, x, y, {
      speed,
      damage: WEATHER_FX.meteor.damage,
      tint: 0xff6644,
      homing: false,
      scale: 1.4,
    });
    this.bossBulletGroup.add(proj);
    proj.body.setVelocity((dx / d) * speed, (dy / d) * speed);
    proj.body.setAllowGravity(false);
  }


  // ─── 暂停 / GameOver / 复活 ───────────────────────────────────────

  private _pauseGame(): void {
    if (this._paused || this._isDead || this.upgradeMgr.isOpen()) return;
    this._paused = true;
    this.physics.pause();
    this.spawner.pause();
    this.difficulty.pause();
    this.panels.showPause(
      {
        score: this.score,
        highScore: this.highScore,
        kills: this.kills,
        wave: this.difficulty.tier + 1,
      },
      {
        onResume: () => this._resumeGame(),
        onRestart: () => {
          this.highScore = saveHighScore(this.score, this._mode);
          this.scene.restart();
        },
        onMenu: () => {
          this._goMenu();
        },
      },
    );
  }

  private _resumeGame(): void {
    if (!this._paused) return;
    this._paused = false;
    this.physics.resume();
    this.spawner.resume();
    this.difficulty.resume();
  }

  private _gameOver(): void {
    this._isDead = true;
    this.physics.pause();
    this.spawner.pause();
    this.difficulty.pause();

    // 死亡反馈：hit-pause + 全屏红 + 玩家位置大爆炸 + 强震
    // physics 已 pause，但 timeScale 不影响 tween / camera shake
    this.physics.world.timeScale = 20;
    this.cameras.main.shake(420, 0.020);
    this.effects.explosion(this.player.x, this.player.y, { big: true, color: 0xff5566, count: 40 });
    // 全屏深红 vignette（持续 1.0s）
    const vignette = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xff2a3a, 0)
      .setDepth(195)
      .setScrollFactor(0);
    this.tweens.add({
      targets: vignette,
      alpha: { from: 0, to: 0.45 },
      duration: 240,
      yoyo: true,
      hold: 600,
      onComplete: () => vignette.destroy(),
    });

    // 缓存"本局开始时的最高分"用来算"差多少破纪录"
    const prevHighScore = this.highScore;
    this.highScore = saveHighScore(this.score, this._mode);
    const beatHighScore = this.score > prevHighScore;
    const gapToHigh = beatHighScore ? this.score - prevHighScore : prevHighScore - this.score;

    // 1.0s 之后才弹 GameOver 面板，让玩家"消化"死亡瞬间
    this.time.delayedCall(1000, () => {
      this.physics.world.timeScale = 1;
      const canRevive = this.player.coins >= PLAYER_FX.reviveCoinCost;
      this.panels.showGameOver(
        {
          score: this.score,
          highScore: this.highScore,
          kills: this.kills,
          wave: this.difficulty.tier + 1,
          coins: this.player.coins,
          canRevive,
          beatHighScore,
          gapToHigh,
        },
        {
          onRevive: () => this._consumeRevive(),
          onRestart: () => this.scene.restart(),
          onMenu: () => this._goMenu(),
        },
      );
    });
  }

  private _consumeRevive(): void {
    this.player.coins -= PLAYER_FX.reviveCoinCost;
    this.player.hp = this.player.maxHp;
    this.player.shieldCharges = Math.max(this.player.shieldCharges, 1);
    this.player._invincibleUntil = performance.now() + 3000;
    this._isDead = false;

    // 不清场：怪物（含 Boss / mini / 弹幕）全部保留，
    // 由 3 秒无敌窗口给玩家走出包围圈的机会。
    this.physics.resume();
    this.spawner.resume();
    this.difficulty.resume();

    this.sound2.upgrade();
    this.hud.refreshHearts();
    this._refreshHud();
  }

  /**
   * 核爆指令：把每只敌人都视作"被打死"走标准死亡流程（计分 / 爆炸 / 掉落都触发），
   * 同时清空 Boss 弹幕。屏幕走一次大震动 + 白色冲击波，给玩家正反馈。
   */
  private _doNuke(): void {
    // 屏幕级冲击：白色全屏闪 + 强震
    const flash = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xffffff, 0.85)
      .setDepth(200)
      .setScrollFactor(0);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 380,
      onComplete: () => flash.destroy(),
    });
    this.cameras.main.shake(260, 0.018);

    // 中央核爆光环
    this.effects.explosion(GAME_WIDTH / 2, GAME_HEIGHT / 2, { big: true, color: 0xffffff, count: 36 });

    // 把所有怪走一次死亡：复用 _onEnemyKilled，让分数 / 金币 / 爆炸都正常发生
    // 注意：_onEnemyKilled 里会 enemy.destroy()，遍历前先 copy 一份避免改集合中迭代
    const enemies = this.enemyGroup.getChildren().slice() as Enemy[];
    for (const enemy of enemies) {
      if (!enemy.active || enemy._dead) continue;
      enemy._dead = true;
      this._onEnemyKilled(enemy);
    }
    // 顺手清掉 Boss 弹幕
    this.bossBulletGroup.clear(true, true);

    this.sound2.bossKill();
    this.hud.setScore(this.score);
  }
}
